import clsx from "clsx";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from "react";

import { Dialog } from "../ui/Dialog";
import { Icon } from "../ui/Icon";
import { IconButton } from "../ui/IconButton";
import { showToast } from "../ui/toastStore";
import "./chatbar.css";

/*
  Adding a document from the chat (rebuild package 1G): drop a file on the chat, or press the paperclip. Either way the
  person lands in the same form, with the file already chosen, and nothing is sent until they press its button. This file
  knows about files and the dialog only. What the form asks, and where the file goes, belongs to the view that gives it.
*/

interface DropControls {
  /** Opens the browser's file picker. */
  pick: () => void;
  disabled: boolean;
}

const Controls = createContext<DropControls | null>(null);

/** The paperclip in the chat box. It works inside a `DropUpload` and shows nothing outside one. */
export function AttachButton({ label = "Add a document" }: { label?: string }) {
  const controls = useContext(Controls);
  if (!controls) return null;
  return (
    <IconButton label={label} onClick={controls.pick} disabled={controls.disabled}>
      <Icon name="paperclip" size={14} />
    </IconButton>
  );
}

/** Whether a file matches an `accept` list of extensions (".pdf"), types ("application/pdf") and groups ("image/*"). No list takes any file. */
export function acceptsFile(file: Pick<File, "name" | "type">, accept: string | undefined): boolean {
  const rules = (accept ?? "")
    .split(",")
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);
  if (rules.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type ?? "").toLowerCase();
  return rules.some((rule) => (rule.startsWith(".") ? name.endsWith(rule) : rule.endsWith("/*") ? type.startsWith(rule.slice(0, -1)) : type === rule));
}

/** A drag that carries files (a dragged piece of text or a link does not). */
function carriesFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

export interface DropUploadProps {
  /** Draws the form for the chosen file. `close` shuts the dialog, after the file is sent or when the person gives up. */
  renderForm: (file: File, close: () => void) => ReactNode;
  /** The heading of the dialog. */
  title?: string;
  /** The files that can be added, as in an `accept` attribute. Other files are turned away with `unsupported`. */
  accept?: string;
  unsupported?: string;
  /** Adding is not possible now (nothing to attach the file to is picked yet, say): a dropped file is ignored, and the paperclip is off. */
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Wraps a part of a view as a place to drop a file, and gives its `AttachButton` something to open. A file dropped, or chosen
 * with the paperclip, opens a dialog with the view's form. One file at a time. While the dialog is open the wrapper leaves
 * drags alone, so a file can be dropped on the form's own file box.
 */
export function DropUpload({ renderForm, title = "Add a document", accept, unsupported = "That kind of file cannot be added here.", disabled = false, className, children }: DropUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const input = useRef<HTMLInputElement>(null);

  const take = useCallback(
    (files: FileList | null | undefined) => {
      const list = Array.from(files ?? []);
      const first = list[0];
      if (!first) return;
      if (!acceptsFile(first, accept)) {
        showToast({ text: unsupported });
        return;
      }
      if (list.length > 1) showToast({ text: "One document at a time. The first file was used." });
      setFile(first);
    },
    [accept, unsupported],
  );

  const controls = useMemo<DropControls>(() => ({ pick: () => input.current?.click(), disabled }), [disabled]);

  // A file dropped on the page opens in the browser and takes the person away from their work, so file drags are always
  // caught here, even when adding is off.
  const onDragEnter = (event: DragEvent) => {
    if (file || !carriesFiles(event)) return;
    event.preventDefault();
    depth.current += 1;
    if (!disabled) setDragging(true);
  };
  const onDragOver = (event: DragEvent) => {
    if (file || !carriesFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = disabled ? "none" : "copy";
  };
  const onDragLeave = (event: DragEvent) => {
    if (file || !carriesFiles(event)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  };
  const onDrop = (event: DragEvent) => {
    if (file || !carriesFiles(event)) return;
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (!disabled) take(event.dataTransfer.files);
  };

  const onChoose = (event: ChangeEvent<HTMLInputElement>) => {
    take(event.target.files);
    // Choosing the same file again must count as a choice.
    event.target.value = "";
  };

  const close = useCallback(() => setFile(null), []);

  return (
    <Controls.Provider value={controls}>
      <div className={clsx("drop-upload", className)} data-dragging={dragging ? "true" : undefined} onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        {children}
        {dragging && (
          <p className="drop-upload__hint" aria-hidden="true">
            Drop the file to add it
          </p>
        )}
        <input ref={input} type="file" hidden tabIndex={-1} accept={accept} aria-label="Choose a document" onChange={onChoose} />
      </div>
      <Dialog open={file !== null} onClose={close} title={title} size="medium" closeOnBackdrop={false}>
        {file && renderForm(file, close)}
      </Dialog>
    </Controls.Provider>
  );
}
